import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Plane, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const EMPTY = { distance: "", travel_date: "", return_date: "", stay: "", notes: "" };

/** Dialog for a joined runner to add/edit their travel + stay plan for a race. */
export default function RaceTravelPlanDialog({ race, onChange, children }) {
  const [open, setOpen] = useState(false);
  const [p, setP] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const options = (race.distances || "").split(/[·,|/]/).map((s) => s.trim()).filter(Boolean);

  useEffect(() => {
    if (open) setP({ ...EMPTY, ...(race.my_plan || {}) });
  }, [open, race.my_plan]);

  const save = async () => {
    setBusy(true);
    try {
      const r = await api.put(`/races/${race.race_id}/plan`, p);
      onChange?.(r.data);
      toast.success("Travel plan saved — Sumit can see it on the roster");
      setOpen(false);
    } catch { toast.error("Could not save plan"); }
    finally { setBusy(false); }
  };

  const remove = async () => {
    setBusy(true);
    try {
      const r = await api.delete(`/races/${race.race_id}/plan`);
      onChange?.(r.data);
      toast.success("Travel plan removed");
      setOpen(false);
    } catch { toast.error("Could not remove plan"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="max-w-lg bg-zinc-950 border-zinc-800 text-white rounded-none" data-testid={`travel-plan-dialog-${race.race_id}`}>
        <DialogHeader>
          <p className="font-mono text-[10px] tracking-[0.3em] text-orange-500 uppercase flex items-center gap-2"><Plane size={11} /> GROUP TRIP · TRAVEL PLAN</p>
          <DialogTitle className="font-display text-2xl text-white tracking-wider">{race.title}</DialogTitle>
          <p className="text-xs text-zinc-400">
            Share how you&apos;re getting there and where you&apos;re staying so the pack can travel, room-share and carpool together.
          </p>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          {options.length > 0 ? (
            <div>
              <Label className="font-display tracking-widest text-xs text-zinc-400">RUNNING WHICH DISTANCE?</Label>
              <div className="mt-2 flex flex-wrap gap-2">
                {options.map((o) => (
                  <button
                    key={o}
                    type="button"
                    data-testid={`plan-distance-${o.replace(/\s+/g, "-").toLowerCase()}`}
                    onClick={() => setP({ ...p, distance: o })}
                    className={`hairline px-3 py-1.5 font-display text-xs tracking-[0.2em] transition ${
                      p.distance === o ? "bg-orange-600 border-orange-500 text-white" : "border-zinc-700 text-zinc-400 hover:text-white"
                    }`}
                  >
                    {o.toUpperCase()}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <F label="DISTANCE" dt="plan-distance" value={p.distance} onChange={(v) => setP({ ...p, distance: v })} placeholder="Half marathon" />
          )}

          <div className="grid grid-cols-2 gap-3">
            <F label="ARRIVING ON" dt="plan-travel-date" type="date" value={p.travel_date} onChange={(v) => setP({ ...p, travel_date: v })} />
            <F label="LEAVING ON" dt="plan-return-date" type="date" value={p.return_date} onChange={(v) => setP({ ...p, return_date: v })} />
          </div>
          <F label="STAYING AT (HOTEL / AREA / WITH FRIENDS)" dt="plan-stay" value={p.stay} onChange={(v) => setP({ ...p, stay: v })} placeholder="Hotel near the start line" />
          <div>
            <Label className="font-display tracking-widest text-xs text-zinc-400">NOTES FOR THE PACK (OPTIONAL)</Label>
            <Textarea
              data-testid="plan-notes"
              value={p.notes}
              onChange={(e) => setP({ ...p, notes: e.target.value })}
              rows={2}
              placeholder="Train / flight details, spare seats, room-share, bib pickup…"
              className="mt-2 rounded-none bg-zinc-950 border-zinc-800 text-white text-sm focus-visible:ring-orange-600"
            />
          </div>

          <div className="flex items-center gap-2 pt-1">
            <Button data-testid="plan-save-btn" onClick={save} disabled={busy} className="flex-1 rounded-none bg-orange-600 hover:bg-orange-500 font-display tracking-wider">
              SAVE PLAN
            </Button>
            {race.my_plan && (
              <Button data-testid="plan-remove-btn" onClick={remove} disabled={busy} variant="outline" className="rounded-none border-zinc-700 bg-transparent text-zinc-300 hover:text-red-400 hover:border-red-500/50">
                <Trash2 size={14} />
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function F({ label, value, onChange, dt, placeholder, type = "text" }) {
  return (
    <div>
      <Label className="font-display tracking-widest text-xs text-zinc-400">{label}</Label>
      <Input data-testid={dt} type={type} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
        className="mt-2 rounded-none bg-zinc-950 border-zinc-800 text-white focus-visible:ring-orange-600" />
    </div>
  );
}

/** Compact one-line summary of a plan, e.g. "HALF · ARRIVE 15 JAN · HOTEL NEAR AZAD MAIDAN". */
export function planSummary(plan) {
  if (!plan) return "";
  const fmt = (d) => (d ? new Date(`${d}T00:00:00`).toLocaleDateString("en-IN", { day: "numeric", month: "short" }) : "");
  return [plan.distance, plan.travel_date && `Arrive ${fmt(plan.travel_date)}`, plan.return_date && `Leave ${fmt(plan.return_date)}`, plan.stay]
    .filter(Boolean)
    .join(" · ");
}
